<template>
  <v-layout no-gutters class="raster-browser-body oct-browser-body escalon-absolute-cover" :style="ieStyle">
    <v-flex shrink xs3 class="sidebar-controls tall-content-wrapper">
      <!--do not change nextSlice, previousSlice. it is intended to keep the same UX as the spectralis-->
      <oct-reference-image
        style="height: 300px;"
        :isLoading="refImgLoading || octDataLoading"
        :refImg="refImg"
        :refFit="refFit"
        :refImgNotFound="refImgNotFound"
        :refImgLocation="refImgLocation"
        :slices="slices"
        :sliceCurrent="sliceCurrent"
        :sliceBounds="sliceBounds"
        :sliceCurrentIndex="sliceCurrentIndex"
        :isPlaying="isPlaying"
        :playRate="playRate"
        :showExtension="true"
        :drawAllSlices="true"
        @previous="nextSlice" 
        @next="previousSlice"
        @play="play"
        @pause="pause"
        @setPlayRate="setPlayRate"
        @selectSlice="selectSlice"/>
        <raster-color-buttons 
          :isInverted="isInverted"
          :hasColorPalette="hasColorPalette"
          @toggleColor="toggleColor" 
          @invertColor="invertColor" />
      <raster-image-drawer 
        :slices="slices"
        :sliceCurrentIndex="sliceCurrentIndex"
        :octDataLoading="octDataLoading"
        @selectSlice="selectSlice"
        :octColorSettings="octColorSettings"
        :rasterThumbSize="rasterThumbSize" />
    </v-flex>
    <v-flex shrink xs9 class="main-view tall-content-wrapper">
      <oct-toolbar />
      <oct-slice-view
        :fitSize="sliceFit"
        :img="sliceImg"
        :imgSize="sliceImgSize"
        :octColorSettings="octColorSettings"
        :imgLoading="sliceImgLoading"
        :dataLoading="octDataLoading"
        :sliceEyeMarker="sliceCurrentEyeMarker"
        :imgNotFound="sliceImgNotFound"
        :sliceInfo="sliceInfo"/>
    </v-flex>
  </v-layout>
</template>

<script>
import octReferenceImage from "./OCTReferenceImage";
import octToolbar from "./OCTToolbar";
import rasterImageDrawer from "./RasterImageDrawer";
import rasterColorButtons from "./RasterColorButtons";
import octSliceView from "./OCTSliceView";
import responsiveCanvases from "../../mixins/ResponsiveCanvases";
import { mapWatchers } from "../../../helpers/utils";
import { defaultSize, getViewSize, setSize, setFittedSize, viewOffset, withinViewBounds, calculateRealLocation, defaultTransform, setTransformSizes } from "../../../canvas/geometry";
import { mapGetters } from "vuex";
export default {
  name: "rasterBrowserBody",
  components: {
    octToolbar,
    octReferenceImage,
    rasterImageDrawer,
    rasterColorButtons,
    octSliceView
  },
  mixins: [responsiveCanvases],
  data(){
    return {
      refSize: defaultSize(),
      refFit: defaultSize(),
      sliceSize: defaultSize(),
      sliceFit: defaultSize(),
      sliceImgSize: defaultSize(),
      rasterThumbSize: defaultSize(),
      transform: defaultTransform(),
      referenceWrapperId: "reference-image-wrapper",
      sliceWrapperId: "slice-image-wrapper",
      drawerId: "raster-thumbnail-drawer"
    };
  },
  computed: {
    ...mapGetters([
      "refImg", "refImgLoading", "refImgNotFound",

      "slices", "sliceCurrent", "sliceBounds", "sliceCurrentIndex", "sliceImg",
      "sliceImgLoading", "sliceCurrentEyeMarker", "sliceImgNotFound", "sliceInfo",
      
      "octColorSettings",

      "isPlaying", "playRate", "isInverted", "hasColorPalette",

      "octDataLoading",

      "isIE",
    
      "seriesType", "cirrusScanDisplayRatio", "sitecirrusScanDisplayRatio"]),
    ieStyle() {
      if (!this.isIE) return null;
      return "margin-top: 35px";
    },
    scanDisplayRatio() {
      if (this.cirrusScanDisplayRatio && this.cirrusScanDisplayRatio != "default")
        return this.cirrusScanDisplayRatio;
      else
        return this.sitecirrusScanDisplayRatio;
    }
  },
  methods: {
    keyboardEvent(e){
      if(e.keyCode == 37 || e.keyCode == 38) {
        this.$store.dispatch("selectNextSlice");
      }else if(e.keyCode == 39 || e.keyCode == 40){
        this.$store.dispatch("selectPreviousSlice");         
      }
    },
    updateCanvasSizes() {
      // reference control size
      let rvs = getViewSize(this.referenceWrapperId);
      setSize(this.refSize, rvs);

      // fit the reference image within at least a 1:1.25 aspect ratio
      let aspectFit = { h: rvs.h, w: Math.min(rvs.w, rvs.h * 1.25) };
      setFittedSize(this.refSize, aspectFit, this.refFit, 15);

      let svs = getViewSize(this.sliceWrapperId);
      setSize(this.sliceSize, svs);
      
      if (this.scanDisplayRatio == "stretch") {
        // fit the slice image within at most a 1.25:1 aspect ratio
        aspectFit = { h: Math.min(svs.h, svs.w * 0.8), w: svs.w };
        setSize(this.sliceImgSize, aspectFit);
      }
      else {
        let aspectRatio = this.sliceInfo.dimensions.pixelWidth > this.sliceInfo.dimensions.pixelHeight ?
          (this.sliceInfo.dimensions.pixelHeight / this.sliceInfo.dimensions.pixelWidth) :
          (this.sliceInfo.dimensions.pixelWidth / this.sliceInfo.dimensions.pixelHeight);
        if (svs.w * aspectRatio < svs.h) {
          setSize(this.sliceImgSize, { w: svs.w, h: svs.w * aspectRatio });
        } else {
          setSize(this.sliceImgSize, { w: svs.h / aspectRatio, h: svs.h });
        }
      }
      
      setFittedSize(this.sliceSize, this.sliceImgSize, this.sliceFit, 15);

      // enforce same aspect ratio for the thumbnails
      let dvs = getViewSize(this.drawerId);
      let maxRoom = { h: 200, w: dvs.w - 30 };
      setFittedSize(maxRoom, aspectFit, this.rasterThumbSize, 5);

      setTransformSizes(this.transform, this.refFit, this.refFit);
    },
    refImgLocation(x, y){
      const offset = viewOffset(this.refSize, this.refFit);
      if (!withinViewBounds(x, y, this.refSize, offset)) return { x: -1, y: -1 };
      let loc = calculateRealLocation(x, y, offset, this.transform);
      return { x: loc.x / this.refFit.w, y: loc.y / this.refFit.h };
    },
    previousSlice() {
      this.$store.dispatch("selectPreviousSlice");
    },
    nextSlice() {
      this.$store.dispatch("selectNextSlice");
    },
    play() {
      this.$store.dispatch("playSlices");
    },
    pause() {
      this.$store.commit("pause");
    },
    setPlayRate(rate) {
      this.$store.commit("setPlayRate", rate);
    },
    selectSlice(index){
      this.$store.dispatch("selectOCTImageByIndex", {keyword: "slice", index});
    },
    toggleColor(){
      this.$store.dispatch("toggleColorPalette");
    },
    invertColor(){ 
      this.$store.dispatch("toggleInverted");
    }
  },
  watch: {
    ...mapWatchers(["refImg", "sliceInfo"], function(){
      this.updateCanvasSizes();
    })
  },
  mounted() {
    document.addEventListener("keydown", this.keyboardEvent);
  },
  destroyed () {
    document.removeEventListener("keydown", this.keyboardEvent);
  }
}
</script>

<style lang="scss" scoped>
.raster-browser-body ::v-deep {
  .sidebar-controls, .main-view {
    min-height: calc(100vh - 39px);
    
    .raster-thumbnail-drawer {
      max-height: calc(100vh - 500px);
    }
  }

  .main-view .oct-slice-view {
    height: calc(100vh - 85px);
  }
}
</style>